// DAS-2173: "Your Income" modal — unified income dashboard for Portfolio Analysis.
// Uses the SAME yield waterfall as Fixed Income Potential (Asset Allocation) so
// numbers are identical for overlapping categories, then adds deposit interest.

import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Paper, Alert, Divider,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, Tooltip, Collapse, IconButton,
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const fmtCurrency = (v) =>
  v == null || isNaN(v) ? '—' : `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtPct = (v) =>
  v == null || isNaN(v) ? '—' : `${Number(v).toFixed(2)}%`;

// Bond / Fixed Income name detection — same regex as PAModal_AssetAllocation
const FI_RE = /bond|treasury|treasuries|fixed.?income|aggregate.?bond|govt|government|tips|inflation.?protect|investment.?grade|high.?yield|corporate.?bond|municipal|muni|credit|duration|coupon|debt|gilts|bund|obligatie|staatsobligatie|bedrijfsobligatie|\babs\b|asset.?backed/i;
const CASH_RE = /savings|deposit|money.?market|spaar|banksparen|geldmarkt/i;

const CATEGORY_COLORS = {
  'Cash Interest':    '#2e7d32',
  Equity:             '#ab47bc',
  Bond:               '#7b1fa2',
  'Equity Fund':      '#ce93d8',
  Alternatives:       '#455a64',
  'Deposit Interest':  '#e65100',
};

const FI_CATEGORY_ORDER = ['Cash Interest', 'Equity', 'Bond', 'Equity Fund', 'Alternatives'];

const YIELD_SOURCE_LABELS = {
  manual: 'Manual',
  coupon: 'Coupon rate',
  'YF dividend': 'Yahoo Finance',
  'div yield': 'Dividend yield',
  'from name': 'Parsed from name',
  'bank interest': 'Bank interest',
};

const couponFromName = (name) => {
  const m = (name || '').match(/(\d+\.?\d*)%/);
  return m ? parseFloat(m[1]) : null;
};

const PAModal_Income = ({
  data = [],
  source = 'portfolio',
  depositInterestData = [],
  yfData = {},
  exposures = [],
}) => {
  const [sortField, setSortField] = useState('income');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedCategory, setExpandedCategory] = useState(null);

  // OI tickers that are deposits — exclude from portfolio rows to avoid double-counting
  const depositOiTickers = useMemo(() => {
    const set = new Set();
    for (const ev of depositInterestData) {
      if (ev.oi_id) set.add(`OI_${ev.oi_id}`.toUpperCase());
    }
    return set;
  }, [depositInterestData]);

  // ─── Portfolio yield income (same waterfall as FIP in Asset Allocation) ───
  const portfolioIncomeRows = useMemo(() => {
    const rows = [];
    const processed = new Set();
    const tickers = [...new Set(data.map(d => d.Ticker).filter(Boolean))];

    const findOrigItem = (ticker) =>
      data.find(d => (d.Ticker || '').toUpperCase() === ticker);

    const findExposure = (ticker) =>
      exposures.find(e => (e.ticker || '').toUpperCase() === ticker);

    const getPositionValue = (d, exp) => {
      const v = parseFloat(d?.Value_eur);
      if (v > 0) return v;
      const qty = parseFloat(d?.Position) || parseFloat(d?.position_size) || 0;
      const price = parseFloat(d?.purchase_cost) || parseFloat(d?.latest_price) || 0;
      if (qty > 0 && price > 0) return qty * price;
      const expVal = parseFloat(exp?.total_amount_exposure);
      if (expVal > 0) return expVal;
      return 0;
    };

    for (const ticker of tickers) {
      const key = ticker.toUpperCase();
      if (processed.has(key)) continue;
      if (depositOiTickers.has(key)) continue;
      processed.add(key);

      const origItem = findOrigItem(key);
      const exp = findExposure(key);
      const yf = yfData[key] || {};
      const positionValue = getPositionValue(origItem, exp);
      if (positionValue <= 0) continue;

      const name = origItem?.Company || exp?.company || ticker;
      const assetType = (origItem?.asset_type || origItem?.Quotetype || exp?.Assettype || '').toUpperCase();

      const isFI = FI_RE.test(name) || assetType === 'BOND';
      const isCash = CASH_RE.test(name) || assetType === 'CASH';
      const isETF = ['ETF', 'MUTUALFUND', 'ETN', 'EQUITY FUND'].includes(assetType);
      const isAlt = /hedge|private.?equity|venture|alternative/i.test(name);

      let yieldPct = null;
      let yieldSource = '';

      if (isFI) {
        // Fixed Income waterfall: manual → coupon → YF dividend → div yield → name parse
        const manualPct = parseFloat(origItem?.manual_percentage);
        const coupon = parseFloat(origItem?.coupon_percentage);
        const yfDiv = parseFloat(yf.Dividend);
        const divPct = parseFloat(origItem?.dividend_percentage);
        const nameCoupon = couponFromName(name);

        if (manualPct > 0) { yieldPct = manualPct; yieldSource = 'manual'; }
        else if (coupon > 0) { yieldPct = coupon; yieldSource = 'coupon'; }
        else if (yfDiv > 0) { yieldPct = yfDiv; yieldSource = 'YF dividend'; }
        else if (divPct > 0) { yieldPct = divPct; yieldSource = 'div yield'; }
        else if (nameCoupon > 0 && nameCoupon <= 20) { yieldPct = nameCoupon; yieldSource = 'from name'; }
      } else if (isCash) {
        // Cash waterfall: manual → bank interest → YF dividend
        const manualPct = parseFloat(origItem?.manual_percentage);
        const bankInterest = parseFloat(origItem?.bank_interest_percentage);
        const yfDiv = parseFloat(yf.Dividend);

        if (manualPct > 0) { yieldPct = manualPct; yieldSource = 'manual'; }
        else if (bankInterest > 0) { yieldPct = bankInterest; yieldSource = 'bank interest'; }
        else if (yfDiv > 0) { yieldPct = yfDiv; yieldSource = 'YF dividend'; }
      } else {
        // Equity / Other waterfall: manual → YF dividend → div yield
        const manualPct = parseFloat(origItem?.manual_percentage);
        const yfDiv = parseFloat(yf.Dividend);
        const divPct = parseFloat(origItem?.dividend_percentage);

        if (manualPct > 0) { yieldPct = manualPct; yieldSource = 'manual'; }
        else if (yfDiv > 0) { yieldPct = yfDiv; yieldSource = 'YF dividend'; }
        else if (divPct > 0) { yieldPct = divPct; yieldSource = 'div yield'; }
      }

      if (!yieldPct || yieldPct <= 0) continue;

      const income = positionValue * (yieldPct / 100);
      let category;
      if (isCash) category = 'Cash Interest';
      else if (isFI) category = 'Bond';
      else if (isAlt) category = 'Alternatives';
      else if (isETF) category = 'Equity Fund';
      else category = 'Equity';

      rows.push({
        ticker,
        name,
        positionValue,
        income,
        rate: yieldPct,
        incomeSource: yieldSource,
        category,
      });
    }

    return rows;
  }, [data, yfData, exposures, depositOiTickers]);

  // ─── Deposit interest (annual, from Other_Investments) ───
  const depositRows = useMemo(() => {
    return depositInterestData.map(ev => {
      const principal = ev.principal || 0;
      const rate = ev.rate || 0;
      return {
        ticker: '—',
        name: ev.label || ev.institution || 'Deposit',
        positionValue: principal,
        income: principal * (rate / 100),
        rate: rate || null,
        incomeSource: `deposit (matures ${ev.date || '?'})`,
        category: 'Deposit Interest',
      };
    });
  }, [depositInterestData]);

  const allRows = useMemo(() => [...portfolioIncomeRows, ...depositRows], [portfolioIncomeRows, depositRows]);

  const portfolioIncomeTotal = useMemo(() => portfolioIncomeRows.reduce((s, r) => s + r.income, 0), [portfolioIncomeRows]);
  const depositIncomeTotal = useMemo(() => depositRows.reduce((s, r) => s + r.income, 0), [depositRows]);
  const totalIncome = portfolioIncomeTotal + depositIncomeTotal;

  const totalPositionValue = useMemo(() => allRows.reduce((s, r) => s + r.positionValue, 0), [allRows]);
  const weightedYield = totalPositionValue > 0 ? (totalIncome / totalPositionValue) * 100 : 0;

  const sortedRows = useMemo(() => {
    return [...allRows].sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'ticker':   va = a.ticker; vb = b.ticker; return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'name':     va = a.name; vb = b.name; return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'category': va = a.category; vb = b.category; return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'value':    va = a.positionValue; vb = b.positionValue; break;
        case 'rate':     va = a.rate || 0; vb = b.rate || 0; break;
        case 'income':
        default:         va = a.income; vb = b.income; break;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [allRows, sortField, sortDir]);

  const categoryBreakdown = useMemo(() => {
    const buckets = {};
    for (const row of allRows) {
      buckets[row.category] = (buckets[row.category] || 0) + row.income;
    }
    const order = [...FI_CATEGORY_ORDER, 'Deposit Interest'];
    return order
      .filter(cat => (buckets[cat] || 0) > 0)
      .map(cat => ({ category: cat, income: buckets[cat], color: CATEGORY_COLORS[cat] || '#9e9e9e' }));
  }, [allRows]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (allRows.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="body2">
          No income data available. Income is derived from dividend yields, bond coupons,
          bank interest, and deposit maturities. Make sure your portfolio has yield-bearing positions.
        </Typography>
      </Alert>
    );
  }

  const pieData = categoryBreakdown.map((d, i) => ({
    id: i,
    value: d.income,
    label: `${d.category} (${fmtCurrency(d.income)})`,
    color: d.color,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ─── Summary header ─── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderColor: '#e65100', borderWidth: 1.5 }}>
        <Box display="flex" alignItems="baseline" gap={2} flexWrap="wrap">
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
            {fmtCurrency(totalIncome)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            estimated annual income
          </Typography>
          <Chip label="estimated" size="small" variant="outlined" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
        </Box>
        <Box display="flex" gap={3} mt={1} flexWrap="wrap" alignItems="center">
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Portfolio yield: <strong>{fmtCurrency(portfolioIncomeTotal)}</strong>
          </Typography>
          {depositIncomeTotal > 0 && (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>+</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Deposits: <strong>{fmtCurrency(depositIncomeTotal)}</strong>
              </Typography>
            </>
          )}
          <Divider orientation="vertical" flexItem />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Weighted yield: <strong>{fmtPct(weightedYield)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Positions: <strong>{allRows.length}</strong>
          </Typography>
        </Box>
      </Paper>

      {/* ─── Breakdown by category ─── */}
      <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
        {pieData.length > 1 && (
          <Box sx={{ minWidth: 260, flex: '0 0 auto' }}>
            <PieChart
              series={[{
                data: pieData,
                innerRadius: 40,
                outerRadius: 90,
                paddingAngle: 2,
                cornerRadius: 4,
              }]}
              width={320}
              height={220}
              slotProps={{ legend: { hidden: true } }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {categoryBreakdown.map(({ category, income, color }) => {
            const pct = totalIncome > 0 ? (income / totalIncome) * 100 : 0;
            const count = allRows.filter(r => r.category === category).length;
            const isExpanded = expandedCategory === category;
            const isDeposit = category === 'Deposit Interest';

            return (
              <Paper
                key={category}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  ...(isDeposit && { borderStyle: 'dashed' }),
                }}
                onClick={() => setExpandedCategory(isExpanded ? null : category)}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{category}</Typography>
                    <Chip label={`${count}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtCurrency(income)}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{pct.toFixed(1)}%</Typography>
                    <IconButton size="small" sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Collapse in={isExpanded}>
                  <TableContainer sx={{ mt: 1 }}>
                    <MuiTable size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Value</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Income</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRows
                          .filter(r => r.category === category)
                          .sort((a, b) => b.income - a.income)
                          .map((r, i) => (
                            <TableRow key={i} hover>
                              <TableCell>{r.name}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.ticker}</Typography>
                              </TableCell>
                              <TableCell align="right">{fmtCurrency(r.positionValue)}</TableCell>
                              <TableCell align="right">{fmtPct(r.rate)}</TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                  {YIELD_SOURCE_LABELS[r.incomeSource] || r.incomeSource || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500 }}>{fmtCurrency(r.income)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </MuiTable>
                  </TableContainer>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* ─── Full detail table ─── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>All Income Positions</Typography>
          <Tooltip title="Portfolio yield income uses the same calculation as Fixed Income Potential (yield% × value). Deposit interest is annual interest on term deposits.">
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <TableContainer sx={{ maxHeight: 450 }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                <SortableCell field="name" label="Name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableCell field="ticker" label="Ticker" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableCell field="category" label="Type" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableCell field="value" label="Value (EUR)" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
                <SortableCell field="rate" label="Rate" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
                <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                <SortableCell field="income" label="Annual Income" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, i) => (
                <TableRow key={`${row.ticker}-${i}`} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.ticker}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.category}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: `${CATEGORY_COLORS[row.category] || '#9e9e9e'}18`,
                        color: CATEGORY_COLORS[row.category] || '#9e9e9e',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{fmtCurrency(row.positionValue)}</TableCell>
                  <TableCell align="right">{fmtPct(row.rate)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {YIELD_SOURCE_LABELS[row.incomeSource] || row.incomeSource || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCurrency(row.income)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
                <TableCell />
                <TableCell>Total</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">{fmtCurrency(totalPositionValue)}</TableCell>
                <TableCell align="right">{fmtPct(weightedYield)}</TableCell>
                <TableCell />
                <TableCell align="right" sx={{ color: '#e65100', fontSize: '0.95rem' }}>{fmtCurrency(totalIncome)}</TableCell>
              </TableRow>
            </TableBody>
          </MuiTable>
        </TableContainer>
      </Paper>
    </Box>
  );
};

const SortableCell = ({ field, label, sortField, sortDir, onSort, align = 'left' }) => (
  <TableCell align={align} sx={{ fontWeight: 700 }}>
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDir : 'asc'}
      onClick={() => onSort(field)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

export default PAModal_Income;
